import {
  useCaseOpenTasks,
  useCaseTaskOverrides,
  useUpsertCaseOverride,
} from "@/api/board/useBoard";
import type {
  CareEventInput,
  DiagnosisEpisodeState,
  RhythmKind,
} from "@/api/careProtocol/CareProtocolApi";
import {
  useCareChronik,
  useCareReadFull,
  useCareWrite,
  useDiagnoses,
  usePublicCareSummary,
  useSubmitCareEntry,
} from "@/api/careProtocol/useCareProtocol";
import { useCaseRelationCapabilities } from "@/api/cases/useCases";
import { OFFLINE_COMMENTS_HINT } from "@/api/caseTable/caseTableDefinition";
import { useCaseComments, useMentionableMembers } from "@/api/comments/useComments";
import { CareSitesApi } from "@/api/careSites/CareSitesApi";
import { useMyCareSites } from "@/api/careSites/useCareSites";
import {
  useCaseHandovers,
  useInitiateHandover,
} from "@/api/handover/useHandover";
import { queryKeys } from "@/api/queryKeys";
import { uploadPackagingPhoto } from "@/api/careProtocol/protocolMediaUpload";
import {
  useMedications,
  usePlanDues,
  useTherapySidebar,
  useTreatments,
} from "@/api/therapy/useTherapy";
import {
  ANIMAL_STATUS_LABEL,
  CARE_EVENT_TYPE_LABEL,
  CARE_PANEL_LABEL,
  CARE_TASK_KIND_LABEL,
  DIAGNOSIS_STATE_LABEL,
  labelOf,
  LOADING_LABEL,
  RHYTHM_KIND_LABEL,
} from "@/constants/uiLabels";
import {
  assessDeathClosure,
  DEATH_CAUSE_DESCRIBE_HINT,
  DEATH_CAUSE_PICK_HINT,
  DEATH_DATE_INVALID_ERROR,
  formatClosureDeceasedLine,
  initialDeathOn,
  isDeathReportClosed,
  type CaseStatus,
  type DeathCause,
} from "@/ui/care/closureDeceased";
import { viewWhileLoading } from "@/ui/loadingState";
import { useTheme } from "@/theme/ThemeProvider";
import { formatTimestampDE, uiErrorMessage } from "@/utils/isoDate";
import { AppButton } from "@/ui/components/AppButton";
import { DeathClosureFields } from "@/ui/components/care/DeathClosureFields";
import { useFocusEntry } from "@/ui/components/care/useFocusEntry";
import { EntryComments } from "@/ui/components/care/EntryComments";
import { HIT_TARGET_MIN } from "@/ui/components/listRowA11y";
import { FloatingLabelInput } from "@/ui/components/FloatingLabelInput";
import { FloatingLabelListPicker } from "@/ui/components/FloatingLabelListPicker";
import { SectionCard } from "@/ui/components/SectionCard";
import { useNetworkStatus } from "@/network/NetworkContext";
import {
  careSaveUiMessage,
  enqueueAttachMedia,
  OFFLINE_SYNC_HINT,
} from "@/offline/sync/gateway";
import { useQuery } from "@tanstack/react-query";
import * as ImagePicker from "expo-image-picker";
import { useMemo, useState } from "react";
import { Text, View } from "react-native";

function newId(): string {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    /* ignore */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const DIAG_STATE_OPTIONS = (
  ["suspicion", "confirmed", "in_treatment", "ruled_out", "closed"] as const
).map((value) => ({ value, label: DIAGNOSIS_STATE_LABEL[value] }));

const STATUS_OPTIONS = [
  { value: "in_care", label: "In Pflege" },
  { value: "hibernating", label: "Überwinterung (intern)" },
];

const RHYTHM_OPTIONS = [
  { value: "interval", label: "Intervall" },
  { value: "daily_times", label: "Feste Uhrzeiten" },
  { value: "once", label: "Einmalig" },
  { value: "as_needed", label: "Bei Bedarf" },
];

type ActionMode = null | "place" | "status" | "release" | "deceased" | "handover";
type TherapyMode =
  | null
  | "dose"
  | "free_med"
  | "treatment"
  | "free_treatment"
  | "plan"
  | "plan_change"
  | "hint"
  | "exam";

export function CareProtocolPanel({
  caseId,
  organizationId,
  careSiteId,
  currentStatus,
  currentPlaceId,
  foundOn = null,
  focusEntry = false,
  onFocusEntry,
}: {
  caseId: string;
  organizationId: string;
  careSiteId: string | null;
  currentStatus: CaseStatus;
  currentPlaceId?: string | null;
  /** Funddatum YYYY-MM-DD, Grenze für das Todesdatum. */
  foundOn?: string | null;
  /** Board-Aktion „Protokoll“: Eingabezeile Gewicht fokussieren. */
  focusEntry?: boolean;
  /** Nach dem ersten Fokus, z. B. focus=entry aus der URL nehmen. */
  onFocusEntry?: () => void;
}) {
  const { theme } = useTheme();
  const { isOnline } = useNetworkStatus();
  const { canReadFull, loading: rightsLoading } = useCareReadFull(caseId);
  const { canWrite } = useCareWrite(caseId);
  const { summary } = usePublicCareSummary(caseId);
  const { entries, loading: chronikLoading, error: chronikError, refetch } =
    useCareChronik(caseId, canReadFull);
  const { comments: caseComments } = useCaseComments(
    caseId,
    canReadFull && isOnline
  );
  const { members: mentionable } = useMentionableMembers(
    caseId,
    canReadFull && isOnline
  );
  const { isOrgAdmin } = useCaseRelationCapabilities(organizationId);
  const { diagnoses } = useDiagnoses(organizationId);
  const { medications } = useMedications(organizationId);
  const { treatments } = useTreatments(organizationId);
  const { sidebar, refetch: refetchSidebar } = useTherapySidebar(caseId, canReadFull);
  const { dues, refetch: refetchDues } = usePlanDues(caseId, canWrite);
  const { tasks: openTasks, loading: tasksLoading, refetch: refetchTasks } = useCaseOpenTasks(
    caseId,
    canWrite
  );
  const { overrides, refetch: refetchOverrides } = useCaseTaskOverrides(
    caseId,
    canWrite
  );
  const upsertOverride = useUpsertCaseOverride(caseId);
  const submit = useSubmitCareEntry(caseId);
  const { sites } = useMyCareSites();
  const initiateHandover = useInitiateHandover();
  const { handovers: caseHandovers, refetch: refetchHandovers } = useCaseHandovers(
    caseId,
    canReadFull
  );
  const [handoverSiteId, setHandoverSiteId] = useState<string | null>(null);
  const [handoverNote, setHandoverNote] = useState("");

  const [weight, setWeight] = useState("");
  const attachWeight = useFocusEntry(focusEntry, onFocusEntry);
  const [note, setNote] = useState("");
  const [diagnosisId, setDiagnosisId] = useState<string | null>(null);
  const [diagState, setDiagState] = useState<string>("suspicion");
  const [message, setMessage] = useState<string | null>(null);
  const [action, setAction] = useState<ActionMode>(null);
  const [therapy, setTherapy] = useState<TherapyMode>(null);
  const [toPlaceId, setToPlaceId] = useState<string | null>(null);
  const [toStatus, setToStatus] = useState<string>("in_care");
  const [closureKind, setClosureKind] = useState("");
  const [closureNotes, setClosureNotes] = useState("");
  const [deathCause, setDeathCause] = useState<DeathCause | null>(null);
  const [deathCauseNote, setDeathCauseNote] = useState("");
  const [diedOn, setDiedOn] = useState<string | null>(() => initialDeathOn());
  const [deathNotes, setDeathNotes] = useState("");
  const [deathAttempt, setDeathAttempt] = useState(false);
  const [deathDateValid, setDeathDateValid] = useState(true);

  const [medId, setMedId] = useState<string | null>(null);
  const [treatmentId, setTreatmentId] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [unit, setUnit] = useState("Tropfen");
  const [route, setRoute] = useState("oral");
  const [freeName, setFreeName] = useState("");
  const [medNote, setMedNote] = useState("");
  const [isAdditional, setIsAdditional] = useState(false);
  const [additionalReason, setAdditionalReason] = useState("");
  const [dueKey, setDueKey] = useState<string | null>(null);
  const [rhythmKind, setRhythmKind] = useState<string>("interval");
  const [everyHours, setEveryHours] = useState("12");
  const [dailyTimes, setDailyTimes] = useState("08:00,20:00");
  const [onceAt, setOnceAt] = useState("");
  const [rhythmNote, setRhythmNote] = useState("");
  const [hintBody, setHintBody] = useState("");
  const [hintWarning, setHintWarning] = useState(false);
  const [examKind, setExamKind] = useState("");
  const [overrideCheckHours, setOverrideCheckHours] = useState("");
  const [overrideWeightHours, setOverrideWeightHours] = useState("");
  const [packagingUri, setPackagingUri] = useState<string | null>(null);
  const [packagingName, setPackagingName] = useState<string | null>(null);

  const nodesQ = useQuery({
    queryKey: queryKeys.careSites.nodes(careSiteId ?? ""),
    queryFn: () => CareSitesApi.listPlaceNodes(careSiteId!),
    enabled: !!careSiteId && action === "place",
  });

  const placeOptions = useMemo(
    () =>
      (nodesQ.data ?? [])
        .filter((n) => n.is_occupiable)
        .map((n) => ({ value: n.id, label: n.name })),
    [nodesQ.data]
  );

  const diagnosisOptions = useMemo(
    () => diagnoses.map((d) => ({ value: d.id, label: d.label })),
    [diagnoses]
  );

  const medOptions = useMemo(
    () => medications.map((m) => ({ value: m.id, label: m.name })),
    [medications]
  );
  const treatmentOptions = useMemo(
    () => treatments.map((t) => ({ value: t.id, label: t.name })),
    [treatments]
  );
  const planOptions = useMemo(
    () =>
      sidebar.plans.map((p) => ({
        value: p.plan_id,
        label: `${p.label} · Rev. ${p.revision_no} · ${labelOf(RHYTHM_KIND_LABEL, p.rhythm_kind)}`,
      })),
    [sidebar.plans]
  );

  const dueOptions = useMemo(() => {
    const kind =
      therapy === "treatment" || therapy === "free_treatment"
        ? "treatment"
        : "medication";
    return dues
      .filter((d) => d.therapy_kind === kind)
      .map((d) => ({
        value: d.occurrence_key,
        label: `${d.label} · ${formatTimestampDE(d.planned_at)}`,
      }));
  }, [dues, therapy]);

  const parallelMedWarning = useMemo(() => {
    if (!medId) return null;
    const same = sidebar.plans.filter((p) => p.medication_id === medId);
    return same.length >= 1
      ? `Hinweis: Es gibt bereits ${same.length} aktive(n) Plan/Pläne für dieses Medikament.`
      : null;
  }, [medId, sidebar.plans]);

  const pickPackagingPhoto = async () => {
    const res = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      quality: 0.85,
    });
    if (res.canceled || !res.assets?.[0]?.uri) return;
    setPackagingUri(res.assets[0].uri);
    setPackagingName(res.assets[0].fileName ?? "verpackung.jpg");
  };

  const maybeUploadPackaging = async (): Promise<string | undefined> => {
    if (!packagingUri) return undefined;
    if (!isOnline) return undefined;
    return uploadPackagingPhoto({
      caseId,
      image: { uri: packagingUri, fileName: packagingName },
    });
  };

  const runSubmit = async (events: CareEventInput[]) => {
    setMessage(null);
    try {
      if (!isOnline) {
        const blocked = events.find((e) =>
          [
            "plan_change",
            "status_change",
            "closure_released",
            "closure_deceased",
            "care_site_change",
          ].includes(e.event_type)
        );
        if (blocked) {
          setMessage("Diese Aktion ist nur online möglich.");
          return;
        }
      }
      const clientEntryId = newId();
      const saved = await submit.mutateAsync({
        clientEntryId,
        recordedAt: new Date().toISOString(),
        events,
      });
      const savedOffline =
        !!saved &&
        typeof saved === "object" &&
        "pending_sync" in saved &&
        (saved as { pending_sync?: boolean }).pending_sync === true;
      if (!isOnline && packagingUri) {
        await enqueueAttachMedia({
          caseId,
          entryClientId: clientEntryId,
          localUri: packagingUri,
          fileName: packagingName,
        });
      }
      setWeight("");
      setNote("");
      setDiagnosisId(null);
      setAction(null);
      setTherapy(null);
      setClosureKind("");
      setClosureNotes("");
      setFreeName("");
      setMedNote("");
      setAdditionalReason("");
      setIsAdditional(false);
      setHintBody("");
      setExamKind("");
      setTreatmentId(null);
      setPlanId(null);
      setDueKey(null);
      setPackagingUri(null);
      setPackagingName(null);
      setMessage(savedOffline ? OFFLINE_SYNC_HINT : "Protokoll gespeichert.");
      if (!savedOffline) {
        await Promise.all([
          refetch(),
          refetchSidebar(),
          refetchDues(),
          refetchTasks(),
        ]);
      }
    } catch (e: unknown) {
      setMessage(careSaveUiMessage(e));
    }
  };

  const onDailyCheck = async () => {
    await runSubmit([
      {
        event_type: "examination",
        exam_kind: "daily_check",
        sort_index: 1,
      },
    ]);
  };

  const onSaveOverrides = async () => {
    setMessage(null);
    try {
      const check = Number(overrideCheckHours.replace(",", "."));
      const weight = Number(overrideWeightHours.replace(",", "."));
      if (overrideCheckHours.trim()) {
        if (!Number.isFinite(check) || check <= 0) {
          setMessage("Kontroll-Intervall muss > 0 sein.");
          return;
        }
        await upsertOverride.mutateAsync({
          caseId,
          kind: "daily_check",
          intervalHours: check,
          isActive: true,
        });
      }
      if (overrideWeightHours.trim()) {
        if (!Number.isFinite(weight) || weight <= 0) {
          setMessage("Wiege-Intervall muss > 0 sein.");
          return;
        }
        await upsertOverride.mutateAsync({
          caseId,
          kind: "weight",
          intervalHours: weight,
          isActive: true,
        });
      }
      if (!overrideCheckHours.trim() && !overrideWeightHours.trim()) {
        setMessage("Bitte mindestens ein Intervall angeben.");
        return;
      }
      await Promise.all([refetchOverrides(), refetchTasks()]);
      setMessage("Fall-Intervalle gespeichert (auditiert).");
    } catch (e: any) {
      setMessage(uiErrorMessage(e, "Override speichern fehlgeschlagen."));
    }
  };

  const onSaveClinical = async () => {
    const events: CareEventInput[] = [];
    const grams = Number(weight.replace(",", "."));
    if (weight.trim() && Number.isFinite(grams) && grams > 0) {
      events.push({ event_type: "weight", weight_grams: grams, sort_index: 1 });
    }
    if (note.trim()) {
      events.push({ event_type: "free_text", body: note.trim(), sort_index: 2 });
    }
    if (diagnosisId) {
      const current = (summary?.active_diagnoses ?? []).find(
        (d) => d.diagnosis_id === diagnosisId
      );
      events.push({
        event_type: "diagnosis_change",
        diagnosis_id: diagnosisId,
        to_state: diagState as DiagnosisEpisodeState,
        expected_from_state: (current?.state as DiagnosisEpisodeState) ?? null,
        sort_index: 3,
      });
    }
    if (events.length === 0) {
      setMessage("Bitte Gewicht, Text oder Diagnose angeben.");
      return;
    }
    await runSubmit(events);
  };

  const buildRhythm = (): { rhythm_kind: RhythmKind; rhythm: Record<string, unknown> } | null => {
    const kind = rhythmKind as RhythmKind;
    if (kind === "interval") {
      const h = Number(everyHours.replace(",", "."));
      if (!Number.isFinite(h) || h <= 0) return null;
      return { rhythm_kind: kind, rhythm: { every_hours: h } };
    }
    if (kind === "daily_times") {
      const times = dailyTimes
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      if (times.length === 0) return null;
      return { rhythm_kind: kind, rhythm: { times } };
    }
    if (kind === "once") {
      if (!onceAt.trim()) return null;
      return { rhythm_kind: kind, rhythm: { at: new Date(onceAt).toISOString() } };
    }
    return { rhythm_kind: "as_needed", rhythm: {} };
  };

  const onSaveTherapy = async () => {
    try {
      if (therapy === "dose") {
        const amt = Number(amount.replace(",", "."));
        if (!medId || !Number.isFinite(amt) || amt <= 0 || !unit.trim()) {
          setMessage("Medikament, Menge und Einheit erforderlich.");
          return;
        }
        const due = dues.find((d) => d.occurrence_key === dueKey);
        if (isAdditional && !additionalReason.trim()) {
          setMessage("Zusätzliche Gabe braucht eine Begründung.");
          return;
        }
        await runSubmit([
          {
            event_type: "medication_dose",
            medication_id: medId,
            amount: amt,
            unit: unit.trim(),
            route: route.trim() || undefined,
            note: medNote.trim() || undefined,
            is_additional: isAdditional,
            additional_reason: isAdditional ? additionalReason.trim() : undefined,
            plan_id: due?.plan_id,
            plan_revision_id: due?.plan_revision_id,
            occurrence_key: due?.occurrence_key,
            planned_at: due?.planned_at,
            sort_index: 1,
          },
        ]);
        return;
      }
      if (therapy === "free_med") {
        const amt = Number(amount.replace(",", "."));
        if (!freeName.trim() || !Number.isFinite(amt) || amt <= 0 || !unit.trim()) {
          setMessage("Name, Menge und Einheit erforderlich.");
          return;
        }
        const photoId = await maybeUploadPackaging();
        await runSubmit([
          {
            event_type: "medication_dose",
            free_name: freeName.trim(),
            amount: amt,
            unit: unit.trim(),
            route: route.trim() || undefined,
            note: medNote.trim() || undefined,
            packaging_photo_media_id: photoId,
            sort_index: 1,
          },
        ]);
        return;
      }
      if (therapy === "treatment") {
        if (!treatmentId) {
          setMessage("Behandlung wählen.");
          return;
        }
        const due = dues.find((d) => d.occurrence_key === dueKey);
        if (isAdditional && !additionalReason.trim()) {
          setMessage("Zusätzliche Gabe braucht eine Begründung.");
          return;
        }
        const amt = Number(amount.replace(",", "."));
        await runSubmit([
          {
            event_type: "treatment",
            treatment_id: treatmentId,
            amount: Number.isFinite(amt) && amt > 0 ? amt : undefined,
            unit: unit.trim() || undefined,
            route: route.trim() || undefined,
            note: medNote.trim() || undefined,
            is_additional: isAdditional,
            additional_reason: isAdditional ? additionalReason.trim() : undefined,
            plan_id: due?.plan_id,
            plan_revision_id: due?.plan_revision_id,
            occurrence_key: due?.occurrence_key,
            planned_at: due?.planned_at,
            sort_index: 1,
          },
        ]);
        return;
      }
      if (therapy === "free_treatment") {
        if (!freeName.trim()) {
          setMessage("Name der Behandlung erforderlich.");
          return;
        }
        const photoId = await maybeUploadPackaging();
        const amt = Number(amount.replace(",", "."));
        await runSubmit([
          {
            event_type: "treatment",
            free_name: freeName.trim(),
            amount: Number.isFinite(amt) && amt > 0 ? amt : undefined,
            unit: unit.trim() || undefined,
            route: route.trim() || undefined,
            note: medNote.trim() || undefined,
            packaging_photo_media_id: photoId,
            sort_index: 1,
          },
        ]);
        return;
      }
      if (therapy === "plan") {
        if (!medId && !treatmentId) {
          setMessage("Medikament oder Behandlung für Plan wählen.");
          return;
        }
        const rhythm = buildRhythm();
        if (!rhythm) {
          setMessage("Rhythmus unvollständig.");
          return;
        }
        const amt = Number(amount.replace(",", "."));
        await runSubmit([
          {
            event_type: "plan_change",
            action: "open",
            medication_id: medId ?? undefined,
            treatment_id: treatmentId ?? undefined,
            dose_amount: Number.isFinite(amt) && amt > 0 ? amt : undefined,
            dose_unit: unit.trim() || undefined,
            route: route.trim() || undefined,
            rhythm_kind: rhythm.rhythm_kind,
            rhythm: rhythm.rhythm,
            rhythm_note: rhythmNote.trim() || undefined,
            started_at: new Date().toISOString(),
            sort_index: 1,
          },
        ]);
        return;
      }
      if (therapy === "plan_change") {
        if (!planId) {
          setMessage("Aktiven Plan wählen.");
          return;
        }
        const rhythm = buildRhythm();
        if (!rhythm) {
          setMessage("Rhythmus unvollständig.");
          return;
        }
        const amt = Number(amount.replace(",", "."));
        await runSubmit([
          {
            event_type: "plan_change",
            action: "change",
            plan_id: planId,
            dose_amount: Number.isFinite(amt) && amt > 0 ? amt : undefined,
            dose_unit: unit.trim() || undefined,
            route: route.trim() || undefined,
            rhythm_kind: rhythm.rhythm_kind,
            rhythm: rhythm.rhythm,
            rhythm_note: rhythmNote.trim() || undefined,
            started_at: new Date().toISOString(),
            sort_index: 1,
          },
        ]);
        return;
      }
      if (therapy === "hint") {
        if (!hintBody.trim()) {
          setMessage("Hinweistext erforderlich.");
          return;
        }
        await runSubmit([
          {
            event_type: "care_hint_change",
            action: "open",
            body: hintBody.trim(),
            is_warning: hintWarning,
            sort_index: 1,
          },
        ]);
        return;
      }
      if (therapy === "exam") {
        if (!examKind.trim()) {
          setMessage("Untersuchungsart angeben.");
          return;
        }
        await runSubmit([
          {
            event_type: "examination",
            exam_kind: examKind.trim(),
            notes: medNote.trim() || undefined,
            sort_index: 1,
          },
        ]);
      }
    } catch (e: any) {
      setMessage(uiErrorMessage(e, "Therapie speichern fehlgeschlagen."));
    }
  };

  const startPlanChange = (planIdValue: string) => {
    const plan = sidebar.plans.find((p) => p.plan_id === planIdValue);
    if (!plan) return;
    setTherapy("plan_change");
    setPlanId(plan.plan_id);
    setAmount(plan.dose_amount != null ? String(plan.dose_amount) : "");
    setUnit(plan.dose_unit ?? "");
    setRoute(plan.route ?? "");
    setRhythmKind(plan.rhythm_kind);
    setRhythmNote(plan.rhythm_note ?? "");
    if (plan.rhythm_kind === "interval") {
      setEveryHours(String((plan.rhythm as any)?.every_hours ?? "12"));
    }
    if (plan.rhythm_kind === "daily_times") {
      const times = (plan.rhythm as any)?.times;
      setDailyTimes(Array.isArray(times) ? times.join(",") : "08:00,20:00");
    }
  };

  const onSaveAction = async () => {
    if (action === "place") {
      if (!toPlaceId) {
        setMessage("Unterbringungsplatz wählen.");
        return;
      }
      await runSubmit([
        {
          event_type: "place_change",
          to_place_id: toPlaceId,
          expected_from_place_id: currentPlaceId ?? null,
          sort_index: 1,
        },
      ]);
      return;
    }
    if (action === "status") {
      await runSubmit([
        {
          event_type: "status_change",
          to_status: toStatus as "in_care" | "hibernating",
          sort_index: 1,
        },
      ]);
      return;
    }
    if (action === "release") {
      if (!closureKind.trim()) {
        setMessage("Auswilderungsart angeben.");
        return;
      }
      await runSubmit([
        {
          event_type: "closure_released",
          release_kind: closureKind.trim(),
          notes: closureNotes.trim() || null,
          sort_index: 1,
        },
      ]);
      return;
    }
    if (action === "deceased") {
      setDeathAttempt(true);
      if (!deathDateValid) {
        setMessage(DEATH_DATE_INVALID_ERROR);
        return;
      }
      const assessed = assessDeathClosure({
        cause: deathCause,
        note: deathCauseNote,
        diedOn,
        notes: deathNotes,
        foundOn,
        today: initialDeathOn(),
      });
      if ("error" in assessed) {
        setMessage(assessed.error);
        return;
      }
      await runSubmit([assessed.event]);
    }
  };

  const endPlan = async (planId: string) => {
    await runSubmit([
      {
        event_type: "plan_change",
        action: "end",
        plan_id: planId,
        end_status: "completed",
        sort_index: 1,
      },
    ]);
  };

  const endHint = async (hintId: string) => {
    await runSubmit([
      {
        event_type: "care_hint_change",
        action: "end",
        hint_id: hintId,
        sort_index: 1,
      },
    ]);
  };

  const deathDescribeMissing =
    action === "deceased" && deathCause === "other" && deathCauseNote.trim().length === 0;
  const deathReportClosed = isDeathReportClosed(currentStatus);

  return (
    <View style={{ gap: theme.space.sm }}>
      <SectionCard title="Öffentliche Kurzinfo" bodyStyle={{ paddingVertical: theme.space.sm }}>
        <Text style={{ color: theme.color.textSecondary, fontSize: 13 }}>
          Aktuelles Gewicht:{" "}
          {summary?.current_weight_grams != null
            ? `${summary.current_weight_grams} g`
            : "–"}
        </Text>
        <Text style={{ color: theme.color.textSecondary, fontSize: 13, marginTop: 4 }}>
          Aktive Diagnosen:{" "}
          {(summary?.active_diagnoses ?? []).map((d) => d.label).join(", ") || "–"}
        </Text>
      </SectionCard>

      {rightsLoading ? (
        <Text style={{ color: theme.color.textSecondary }}>Rechte werden geprüft…</Text>
      ) : !canReadFull ? (
        <SectionCard title="Pflegechronik" bodyStyle={{ paddingVertical: theme.space.sm }}>
          <Text style={{ color: theme.color.textSecondary }}>
            Die Pflegechronik sowie Hinweise und Behandlungspläne sind nur mit
            vollem Pflegezugriff sichtbar. Als allgemeines Mitglied siehst du
            oben die öffentlichen Kurzinfos – ohne Hinweis auf Existenz von
            Plänen oder Pflegehinweisen.
          </Text>
        </SectionCard>
      ) : (
        <>
          {!canWrite ? (
            <SectionCard title="Ehemaliger Pflegezugriff" bodyStyle={{ paddingVertical: theme.space.sm }}>
              <Text style={{ color: theme.color.textSecondary, fontSize: 13 }}>
                Du hast Leserecht als ehemalige Pflegeperson (Former Caregiver).
                Aktuelle Pflegeaufgaben und Schreibaktionen sind nicht verfügbar.
              </Text>
            </SectionCard>
          ) : null}

          {canWrite ? (
          <>
          <SectionCard title="Offene Pflegeaufgaben" bodyStyle={{ paddingVertical: theme.space.sm }}>
            {viewWhileLoading(tasksLoading, openTasks.length === 0) === "loading" ? (
              <Text style={{ color: theme.color.textSecondary, fontSize: 13 }}>
                {LOADING_LABEL}
              </Text>
            ) : openTasks.length === 0 ? (
              <Text style={{ color: theme.color.textSecondary, fontSize: 13 }}>
                {CARE_PANEL_LABEL.openTasksEmpty}
              </Text>
            ) : (
              <View style={{ gap: 6, marginBottom: theme.space.sm }}>
                {openTasks.map((t, idx) => (
                  <Text
                    key={`${t.kind}-${t.occurrence_key ?? idx}`}
                    style={{
                      color: theme.color.text,
                      fontWeight: t.kind === "daily_check" ? "700" : "500",
                      fontSize: 13,
                    }}
                  >
                    {t.label}
                    {t.due_at
                      ? ` · fällig ${formatTimestampDE(t.due_at)}`
                      : ""}
                  </Text>
                ))}
              </View>
            )}
            <AppButton
              onPress={onDailyCheck}
              disabled={submit.isPending}
            >
              Kontrolle durchgeführt
            </AppButton>
            <Text
              style={{
                color: theme.color.muted,
                fontSize: 12,
                marginTop: theme.space.xs,
              }}
            >
              {CARE_PANEL_LABEL.dailyCheckHint}
            </Text>
          </SectionCard>

          <SectionCard
            title={CARE_PANEL_LABEL.intervalsTitle}
            bodyStyle={{ paddingVertical: theme.space.sm }}
          >
            <Text
              style={{
                color: theme.color.textSecondary,
                fontSize: 13,
                marginBottom: 8,
              }}
            >
              {CARE_PANEL_LABEL.intervalsHint}
            </Text>
            {overrides.map((o: any) => (
              <Text
                key={o.id}
                style={{ color: theme.color.muted, fontSize: 12, marginBottom: 4 }}
              >
                {labelOf(CARE_TASK_KIND_LABEL, o.kind)}: alle{" "}
                {o.interval_hours} h
                {o.is_active ? "" : ` (${CARE_PANEL_LABEL.inactive})`}
              </Text>
            ))}
            <View style={{ gap: theme.space.sm }}>
              <FloatingLabelInput
                label={CARE_PANEL_LABEL.checkEveryHours}
                value={overrideCheckHours}
                onChangeText={setOverrideCheckHours}
              />
              <FloatingLabelInput
                label={CARE_PANEL_LABEL.weightEveryHours}
                value={overrideWeightHours}
                onChangeText={setOverrideWeightHours}
              />
              <AppButton
                variant="outline"
                onPress={onSaveOverrides}
                disabled={upsertOverride.isPending}
              >
                {CARE_PANEL_LABEL.saveIntervals}
              </AppButton>
            </View>
          </SectionCard>

          <SectionCard title={CARE_PANEL_LABEL.therapyTitle} bodyStyle={{ paddingVertical: theme.space.sm }}>
            <Text style={{ color: theme.color.textSecondary, fontSize: 13, marginBottom: 8 }}>
              {CARE_PANEL_LABEL.therapyHint}
            </Text>
            {(sidebar.hints ?? []).map((h) => (
              <View key={h.hint_id} style={{ marginBottom: 8, gap: 4 }}>
                <Text style={{ color: theme.color.text, fontWeight: "600" }}>
                  {h.is_warning ? "⚠ " : ""}
                  {h.body}
                </Text>
                <AppButton variant="outline" onPress={() => endHint(h.hint_id)}>
                  Hinweis beenden
                </AppButton>
              </View>
            ))}
            {(sidebar.plans ?? []).map((p) => (
              <View
                key={p.plan_id}
                style={{
                  borderWidth: 1,
                  borderColor: theme.color.border,
                  borderRadius: theme.radius.md,
                  padding: theme.space.sm,
                  marginBottom: 8,
                  gap: 4,
                }}
              >
                <Text style={{ color: theme.color.text, fontWeight: "700" }}>{p.label}</Text>
                <Text style={{ color: theme.color.textSecondary, fontSize: 12 }}>
                  {p.dose_amount != null ? `${p.dose_amount} ${p.dose_unit ?? ""} · ` : ""}
                  {labelOf(RHYTHM_KIND_LABEL, p.rhythm_kind)}
                  {p.rhythm_note ? ` · ${p.rhythm_note}` : ""}
                </Text>
                <View style={{ flexDirection: "row", gap: theme.space.xs, flexWrap: "wrap" }}>
                  <AppButton variant="outline" onPress={() => startPlanChange(p.plan_id)}>
                    Plan ändern
                  </AppButton>
                  <AppButton variant="outline" onPress={() => endPlan(p.plan_id)}>
                    Plan beenden
                  </AppButton>
                </View>
              </View>
            ))}
            {(sidebar.plans ?? []).length === 0 && (sidebar.hints ?? []).length === 0 ? (
              <Text style={{ color: theme.color.textSecondary }}>Keine aktiven Pläne/Hinweise.</Text>
            ) : null}
            {(dues ?? []).length > 0 ? (
              <Text style={{ color: theme.color.muted, fontSize: 12, marginTop: 4 }}>
                Fälligkeiten (72h): {dues.length}
              </Text>
            ) : null}
          </SectionCard>

          <SectionCard title="Protokollieren" bodyStyle={{ paddingVertical: theme.space.sm }}>
            <View style={{ gap: theme.space.sm }}>
              <FloatingLabelInput
                ref={attachWeight}
                label="Gewicht (g)"
                value={weight}
                onChangeText={setWeight}
                accessibilityLabel="Gewicht (g), Eingabezeile"
                emphasisFocus
              />
              <FloatingLabelInput
                label="Freitext"
                value={note}
                onChangeText={setNote}
              />
              <FloatingLabelListPicker
                label="Diagnose"
                options={[{ value: null, label: "Keine Änderung" }, ...diagnosisOptions]}
                value={diagnosisId}
                onChange={setDiagnosisId}
              />
              {diagnosisId ? (
                <FloatingLabelListPicker
                  label="Diagnosezustand"
                  options={DIAG_STATE_OPTIONS}
                  value={diagState}
                  onChange={(v) => setDiagState(v ?? "suspicion")}
                />
              ) : null}
              <AppButton onPress={onSaveClinical} disabled={submit.isPending}>
                {submit.isPending ? "Speichern…" : "Eintrag speichern"}
              </AppButton>
            </View>
          </SectionCard>

          <SectionCard title="Therapie / Medikation" bodyStyle={{ paddingVertical: theme.space.sm }}>
            <View style={{ flexDirection: "row", flexWrap: "wrap", gap: theme.space.xs, marginBottom: theme.space.sm }}>
              <AppButton variant="outline" onPress={() => setTherapy("dose")}>
                Medikamentengabe
              </AppButton>
              <AppButton variant="outline" onPress={() => setTherapy("free_med")}>
                Freies Medikament
              </AppButton>
              <AppButton variant="outline" onPress={() => setTherapy("treatment")}>
                Behandlung
              </AppButton>
              <AppButton variant="outline" onPress={() => setTherapy("free_treatment")}>
                Freie Behandlung
              </AppButton>
              <AppButton variant="outline" onPress={() => setTherapy("plan")}>
                Plan öffnen
              </AppButton>
              <AppButton variant="outline" onPress={() => setTherapy("plan_change")}>
                Plan ändern
              </AppButton>
              <AppButton variant="outline" onPress={() => setTherapy("hint")}>
                Hinweis
              </AppButton>
              <AppButton variant="outline" onPress={() => setTherapy("exam")}>
                Untersuchung
              </AppButton>
            </View>
            {therapy ? (
              <View style={{ gap: theme.space.sm }}>
                {therapy === "dose" || therapy === "plan" ? (
                  <FloatingLabelListPicker
                    label="Medikament"
                    options={medOptions}
                    value={medId}
                    onChange={(v) => {
                      setMedId(v);
                      if (v) setTreatmentId(null);
                    }}
                    placeholder="Wählen…"
                  />
                ) : null}
                {therapy === "plan" ? (
                  <FloatingLabelListPicker
                    label="Oder Behandlung"
                    options={[{ value: null, label: "–" }, ...treatmentOptions]}
                    value={treatmentId}
                    onChange={(v) => {
                      setTreatmentId(v);
                      if (v) setMedId(null);
                    }}
                  />
                ) : null}
                {therapy === "treatment" ? (
                  <FloatingLabelListPicker
                    label="Behandlung"
                    options={treatmentOptions}
                    value={treatmentId}
                    onChange={setTreatmentId}
                    placeholder="Wählen…"
                  />
                ) : null}
                {therapy === "plan_change" ? (
                  <FloatingLabelListPicker
                    label="Aktiver Plan"
                    options={planOptions}
                    value={planId}
                    onChange={(v) => {
                      if (v) startPlanChange(v);
                      else setPlanId(null);
                    }}
                    placeholder="Plan wählen…"
                  />
                ) : null}
                {parallelMedWarning && therapy === "plan" ? (
                  <Text style={{ color: theme.color.dangerText, fontSize: 12 }}>
                    {parallelMedWarning}
                  </Text>
                ) : null}
                {therapy === "free_med" || therapy === "free_treatment" ? (
                  <FloatingLabelInput
                    label="Name (Freitext)"
                    value={freeName}
                    onChangeText={setFreeName}
                  />
                ) : null}
                {therapy === "dose" ||
                therapy === "free_med" ||
                therapy === "treatment" ||
                therapy === "free_treatment" ||
                therapy === "plan" ||
                therapy === "plan_change" ? (
                  <>
                    <FloatingLabelInput label="Menge" value={amount} onChangeText={setAmount} />
                    <FloatingLabelInput label="Einheit" value={unit} onChangeText={setUnit} />
                    <FloatingLabelInput label="Route" value={route} onChangeText={setRoute} />
                  </>
                ) : null}
                {therapy === "dose" || therapy === "treatment" ? (
                  <>
                    <FloatingLabelListPicker
                      label="Fälligkeit / Planbezug (optional)"
                      options={[{ value: null, label: "Ohne Planbezug" }, ...dueOptions]}
                      value={dueKey}
                      onChange={setDueKey}
                    />
                    <AppButton
                      variant={isAdditional ? "primary" : "outline"}
                      onPress={() => setIsAdditional((v) => !v)}
                    >
                      {isAdditional
                        ? "Zusätzliche Gabe (aktiv)"
                        : "Als zusätzliche Gabe markieren"}
                    </AppButton>
                    {isAdditional ? (
                      <FloatingLabelInput
                        label="Begründung Zusatzgabe"
                        value={additionalReason}
                        onChangeText={setAdditionalReason}
                      />
                    ) : null}
                  </>
                ) : null}
                {therapy === "plan" || therapy === "plan_change" ? (
                  <>
                    <FloatingLabelListPicker
                      label="Rhythmus"
                      options={RHYTHM_OPTIONS}
                      value={rhythmKind}
                      onChange={(v) => setRhythmKind(v ?? "interval")}
                    />
                    {rhythmKind === "interval" ? (
                      <FloatingLabelInput
                        label="Alle X Stunden"
                        value={everyHours}
                        onChangeText={setEveryHours}
                      />
                    ) : null}
                    {rhythmKind === "daily_times" ? (
                      <FloatingLabelInput
                        label="Uhrzeiten (HH:MM, …)"
                        value={dailyTimes}
                        onChangeText={setDailyTimes}
                      />
                    ) : null}
                    {rhythmKind === "once" ? (
                      <FloatingLabelInput
                        label="Zeitpunkt (ISO/lokal)"
                        value={onceAt}
                        onChangeText={setOnceAt}
                      />
                    ) : null}
                    <FloatingLabelInput
                      label="Rhythmus-Notiz (Freitext)"
                      value={rhythmNote}
                      onChangeText={setRhythmNote}
                    />
                    {therapy === "plan_change" ? (
                      <Text style={{ color: theme.color.muted, fontSize: 12 }}>
                        Speichern erzeugt eine neue immutable Planrevision über
                        submit_care_entry – bestehende Revisionen bleiben unverändert.
                      </Text>
                    ) : null}
                  </>
                ) : null}
                {therapy === "free_med" || therapy === "free_treatment" ? (
                  <View style={{ gap: theme.space.xs }}>
                    <AppButton variant="outline" onPress={pickPackagingPhoto}>
                      {packagingUri
                        ? "Verpackungsfoto gewählt – ändern"
                        : "Verpackungsfoto (optional)"}
                    </AppButton>
                    {packagingUri ? (
                      <Text style={{ color: theme.color.muted, fontSize: 12 }}>
                        {packagingName ?? "Foto bereit"}
                      </Text>
                    ) : null}
                  </View>
                ) : null}
                {therapy === "hint" ? (
                  <>
                    <FloatingLabelInput
                      label="Hinweis"
                      value={hintBody}
                      onChangeText={setHintBody}
                    />
                    <AppButton
                      variant={hintWarning ? "primary" : "outline"}
                      onPress={() => setHintWarning((v) => !v)}
                    >
                      {hintWarning ? "Als Warnung markiert" : "Als Warnung markieren"}
                    </AppButton>
                  </>
                ) : null}
                {therapy === "exam" ? (
                  <FloatingLabelInput
                    label="Untersuchungsart"
                    value={examKind}
                    onChangeText={setExamKind}
                  />
                ) : null}
                {therapy === "dose" ||
                therapy === "free_med" ||
                therapy === "treatment" ||
                therapy === "free_treatment" ||
                therapy === "exam" ? (
                  <FloatingLabelInput
                    label="Bemerkung"
                    value={medNote}
                    onChangeText={setMedNote}
                  />
                ) : null}
                <View style={{ flexDirection: "row", gap: theme.space.sm }}>
                  <AppButton variant="secondary" onPress={() => setTherapy(null)}>
                    Abbrechen
                  </AppButton>
                  <AppButton onPress={onSaveTherapy} disabled={submit.isPending}>
                    Speichern
                  </AppButton>
                </View>
              </View>
            ) : null}
          </SectionCard>

          <SectionCard
            title="Unterbringung / Status / Abschluss"
            bodyStyle={{ paddingVertical: theme.space.sm }}
          >
            <Text
              style={{
                color: theme.color.textSecondary,
                fontSize: 13,
                marginBottom: theme.space.sm,
              }}
            >
              {CARE_PANEL_LABEL.housingHint}
            </Text>
            <View style={{ gap: theme.space.xs, marginBottom: theme.space.sm }}>
              <AppButton variant="outline" onPress={() => setAction("place")}>
                Unterbringungsplatz ändern
              </AppButton>
              <AppButton variant="outline" onPress={() => setAction("status")}>
                Status ändern
              </AppButton>
              <AppButton
                variant="outline"
                accessibilityRole="button"
                accessibilityLabel="An andere Pflegestelle übergeben"
                onPress={() => setAction("handover")}
              >
                An andere Pflegestelle übergeben
              </AppButton>
              <AppButton variant="outline" onPress={() => setAction("release")}>
                Auswildern
              </AppButton>
              {deathReportClosed ? null : (
                <AppButton
                  variant="outline"
                  accessibilityRole="button"
                  style={{ minHeight: HIT_TARGET_MIN }}
                  onPress={() => {
                    setDeathCause(null);
                    setDeathCauseNote("");
                    setDiedOn(initialDeathOn());
                    setDeathNotes("");
                    setDeathAttempt(false);
                    setDeathDateValid(true);
                    setAction("deceased");
                  }}
                >
                  Verstorben melden
                </AppButton>
              )}
            </View>
            {action ? (
              <View style={{ gap: theme.space.sm }}>
                {action === "place" ? (
                  <FloatingLabelListPicker
                    label="Neuer Unterbringungsplatz"
                    options={placeOptions}
                    value={toPlaceId}
                    onChange={setToPlaceId}
                    placeholder="Platz wählen…"
                  />
                ) : null}
                {action === "status" ? (
                  <FloatingLabelListPicker
                    label="Neuer Status"
                    options={STATUS_OPTIONS}
                    value={toStatus}
                    onChange={(v) => setToStatus(v ?? "in_care")}
                  />
                ) : null}
                {action === "handover" ? (
                  <>
                    <FloatingLabelListPicker
                      label="Ziel-Pflegestelle"
                      options={sites
                        .filter((s) => s.id !== careSiteId)
                        .map((s) => ({ value: s.id, label: s.name }))}
                      value={handoverSiteId}
                      onChange={setHandoverSiteId}
                      placeholder="Pflegestelle wählen…"
                    />
                    <FloatingLabelInput
                      label="Notiz (optional)"
                      value={handoverNote}
                      onChangeText={setHandoverNote}
                    />
                    <Text style={{ color: theme.color.muted, fontSize: 12 }}>
                      Die Zielseite muss annehmen. Platz kann bei Annahme
                      gewählt werden (sonst „Noch nicht zugeordnet“).
                    </Text>
                  </>
                ) : null}
                {action === "release" ? (
                  <>
                    <FloatingLabelInput
                      label="Auswilderungsart"
                      value={closureKind}
                      onChangeText={setClosureKind}
                    />
                    <FloatingLabelInput
                      label="Notiz"
                      value={closureNotes}
                      onChangeText={setClosureNotes}
                    />
                  </>
                ) : null}
                {action === "deceased" ? (
                  <DeathClosureFields
                    cause={deathCause}
                    onCauseChange={setDeathCause}
                    note={deathCauseNote}
                    onNoteChange={setDeathCauseNote}
                    diedOn={diedOn}
                    onDiedOnChange={setDiedOn}
                    notes={deathNotes}
                    onNotesChange={setDeathNotes}
                    foundOn={foundOn}
                    todayIso={initialDeathOn()}
                    showErrors={deathAttempt}
                    onValidityChange={setDeathDateValid}
                  />
                ) : null}
                {action === "deceased" && deathCause == null ? (
                  <Text style={{ color: theme.color.dangerText, fontSize: theme.fontSize.tiny }}>
                    {DEATH_CAUSE_PICK_HINT}
                  </Text>
                ) : null}
                {deathDescribeMissing ? (
                  <Text
                    accessibilityRole="alert"
                    accessibilityLiveRegion="assertive"
                    style={{ color: theme.color.dangerText, fontSize: theme.fontSize.tiny }}
                  >
                    {DEATH_CAUSE_DESCRIBE_HINT}
                  </Text>
                ) : null}
                <View style={{ flexDirection: "row", gap: theme.space.sm }}>
                  <AppButton
                    variant="secondary"
                    accessibilityRole="button"
                    style={{ minHeight: HIT_TARGET_MIN }}
                    onPress={() => setAction(null)}
                  >
                    Abbrechen
                  </AppButton>
                  {action === "handover" ? (
                    <AppButton
                      accessibilityRole="button"
                      accessibilityLabel="Übergabe anfragen"
                      onPress={async () => {
                        if (!handoverSiteId) {
                          setMessage("Ziel-Pflegestelle wählen.");
                          return;
                        }
                        try {
                          await initiateHandover.mutateAsync({
                            caseIds: [caseId],
                            toCareSiteId: handoverSiteId,
                            note: handoverNote || null,
                          });
                          setAction(null);
                          setHandoverSiteId(null);
                          setHandoverNote("");
                          setMessage("Übergabe angefragt.");
                          await refetchHandovers();
                        } catch (e: any) {
                          setMessage(uiErrorMessage(e, "Übergabe fehlgeschlagen."));
                        }
                      }}
                      disabled={initiateHandover.isPending}
                    >
                      Übergabe anfragen
                    </AppButton>
                  ) : (
                    <AppButton
                      onPress={onSaveAction}
                      disabled={
                        submit.isPending ||
                        (action === "deceased" &&
                          (!deathDateValid || deathCause == null || deathDescribeMissing))
                      }
                      accessibilityRole="button"
                      style={{ minHeight: HIT_TARGET_MIN }}
                    >
                      Ausführen
                    </AppButton>
                  )}
                </View>
                <Text style={{ color: theme.color.muted, fontSize: 12 }}>
                  Aktueller Status: {currentStatus}
                </Text>
              </View>
            ) : null}
            {(caseHandovers ?? []).filter((h) => h.status === "pending").length >
            0 ? (
              <Text style={{ color: theme.color.primary, fontSize: 12, marginTop: 8 }}>
                Offene Übergabe angefragt – wartet auf Annahme der Zielseite.
              </Text>
            ) : null}
          </SectionCard>
          </>
          ) : null}

          <SectionCard title="Chronik" bodyStyle={{ paddingVertical: theme.space.sm }}>
            {canReadFull && !isOnline ? (
              <Text
                style={{
                  color: theme.color.muted,
                  fontSize: 13,
                  marginBottom: theme.space.sm,
                }}
              >
                {OFFLINE_COMMENTS_HINT()}
              </Text>
            ) : null}
            {chronikLoading ? (
              <Text style={{ color: theme.color.textSecondary }}>Laden…</Text>
            ) : chronikError ? (
              <Text style={{ color: theme.color.dangerText }}>
                {careSaveUiMessage(chronikError)}
              </Text>
            ) : entries.length === 0 ? (
              <Text style={{ color: theme.color.textSecondary }}>
                Noch keine Einträge.
              </Text>
            ) : (
              <View>
                {entries.map((e, i) => (
                  <View
                    key={e.id}
                    style={{
                      flexDirection: "row",
                      gap: theme.space.sm,
                      paddingVertical: theme.space.sm,
                      borderBottomWidth: i === entries.length - 1 ? 0 : 1,
                      borderBottomColor: theme.color.border,
                    }}
                  >
                    <View
                      style={{
                        width: 3,
                        borderRadius: 2,
                        backgroundColor: e.is_erroneous
                          ? theme.color.danger
                          : theme.color.primary,
                        marginTop: 2,
                        alignSelf: "stretch",
                      }}
                    />
                    <View style={{ flex: 1, gap: 2, minWidth: 0 }}>
                      <Text
                        style={{
                          color: theme.color.muted,
                          fontSize: theme.fontSize.tiny,
                          fontWeight: "600",
                        }}
                      >
                        {formatTimestampDE(e.recorded_at)}
                        {e.is_erroneous ? " · fehlerhaft" : ""}
                      </Text>
                      {(e.events ?? []).map((ev: any, idx: number) => (
                        <Text
                          key={`${e.id}-${idx}`}
                          style={{
                            color: theme.color.text,
                            fontSize: theme.fontSize.small,
                            fontWeight: "500",
                          }}
                        >
                          {formatEventLine(ev)}
                        </Text>
                      ))}
                      {canReadFull && isOnline ? (
                        <EntryComments
                          caseId={caseId}
                          entryId={e.id}
                          comments={caseComments.filter((c) => c.entry_id === e.id)}
                          members={mentionable}
                          isOrgAdmin={isOrgAdmin}
                        />
                      ) : null}
                    </View>
                  </View>
                ))}
              </View>
            )}
          </SectionCard>
        </>
      )}

      {message ? (
        <Text style={{ color: theme.color.muted, fontSize: 13 }}>{message}</Text>
      ) : null}
    </View>
  );
}

function formatEventLine(ev: any): string {
  switch (ev.event_type) {
    case "weight":
      return `Gewicht: ${ev.weight_grams} g`;
    case "free_text":
      return String(ev.body ?? "");
    case "diagnosis_change":
      return `Diagnose ${ev.diagnosis_label ?? ""} → ${labelOf(
        DIAGNOSIS_STATE_LABEL,
        ev.to_state,
      )}`;
    case "place_change":
      return "Unterbringungsplatz geändert";
    case "status_change": {
      const from = labelOf(ANIMAL_STATUS_LABEL, ev.from_status);
      const to = labelOf(ANIMAL_STATUS_LABEL, ev.to_status);
      return `Status: ${from} → ${to}`;
    }
    case "closure_released":
      return `Ausgewildert${ev.release_kind ? ` (${ev.release_kind})` : ""}`;
    case "closure_deceased":
      return formatClosureDeceasedLine(ev);
    case "intake_snapshot":
      return "Erstaufnahme";
    case "image_ref":
      return "Bild";
    case "medication_dose":
      return `Medikament: ${ev.medication_label ?? ""} ${ev.amount ?? ""} ${ev.unit ?? ""}${
        ev.is_additional ? " (zusätzlich)" : ""
      }`;
    case "treatment":
      return `Behandlung: ${ev.treatment_label ?? ""}`;
    case "examination":
      return `Untersuchung${ev.exam_kind ? `: ${ev.exam_kind}` : ""}`;
    case "plan_change":
      return `Plan${ev.plan_action ? ` ${ev.plan_action}` : ""}`;
    case "care_hint_change":
      return `Hinweis${ev.hint_action ? ` ${ev.hint_action}` : ""}${
        ev.body ? `: ${ev.body}` : ""
      }`;
    case "care_site_change":
      return `Pflegestelle gewechselt${
        ev.to_place_id ? "" : " (Platz noch nicht zugeordnet)"
      }`;
    default:
      return labelOf(CARE_EVENT_TYPE_LABEL, ev.event_type, "Ereignis");
  }
}
